import { getLevelPath } from "../station-addressing";
import { ENVELOPE_LAYOUT_VERSION, LEVEL_SPACINGS, POINTS_PER_TILE } from "./envelope-config";
/** Duration of one tile in seconds at the given level. */
export function getTileDuration(level) {
    return LEVEL_SPACINGS[level] * POINTS_PER_TILE[level];
}
/**
 * Returns the tile index that contains the given timestamp (seconds since Unix epoch).
 */
export function getTileIndex(timestamp, level) {
    return Math.floor(timestamp / getTileDuration(level));
}
/**
 * Returns the time range [start, end) in seconds for a given tile.
 */
export function getTileTimeRange(level, tileIndex) {
    const duration = getTileDuration(level);
    return {
        start: tileIndex * duration,
        end: (tileIndex + 1) * duration,
    };
}
/**
 * Returns all tile indices that overlap the viewport [startTime, endTime).
 * Returns an empty array if startTime >= endTime.
 */
export function getTileIndicesForViewport(startTime, endTime, level) {
    if (startTime >= endTime)
        return [];
    const firstTile = getTileIndex(startTime, level);
    // endTime is exclusive — subtract a tiny epsilon so we don't include the next tile
    // if endTime falls exactly on a tile boundary.
    const lastTile = getTileIndex(endTime - 1e-9, level);
    const indices = [];
    for (let i = firstTile; i <= lastTile; i++) {
        indices.push(i);
    }
    return indices;
}
/**
 * Returns the point index within a tile for a given timestamp.
 */
export function getPointIndexInTile(timestamp, level, tileIndex) {
    const tileRange = getTileTimeRange(level, tileIndex);
    return Math.floor((timestamp - tileRange.start) / LEVEL_SPACINGS[level]);
}
/**
 * Constructs the S3 object key for a tile.
 * Format: {network}_{station}/{location}/{channel}/L{level}/{tileIndex}
 */
export function getTileS3Key(stationData, level, tileIndex) {
    return `${getLevelPath(stationData, level)}/${tileIndex}`;
}
/**
 * Constructs the S3 root for tile keys, which includes the current layout version.
 */
export function getS3Root(prefix) {
    return `${prefix}v${ENVELOPE_LAYOUT_VERSION}/`;
}
//# sourceMappingURL=data:application/json;base64,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